import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useMemberAuth } from '../../../contexts/MemberAuthContext';
import { mfetch, MemberApiError } from '../api';
import Icon from '../design/Icon';
import { Sheet, Stepper, addDaysStr, fmtDateShort, fmtDateLong } from '../design/atoms';

/* Pause — the member's plan and packs at the active gym, each with the gym's
   pause rules. A member pause is approved at once, within those rules; a
   pause that has not started can be withdrawn, a running one ended today.
   The server judges every rule — this page explains them first. Mirrors the
   app's Pause screen (same API). */

interface Rules { enabled: boolean; maxTotalDays: number | null; requireReason: boolean; extendsValidity: boolean; memberCanPause: boolean }
interface PauseRow { id: string; startDate: string; endDate: string | null; phase: 'scheduled' | 'active' | 'completed'; reason: string | null; extendedDays: number; byMember: boolean }
interface Pausable {
  kind: 'membership' | 'pack'; id: string; name: string; validTo: string | null; detail: string; rules: Rules;
  usedDays: number; remainingDays: number | null; weeklySlots: number; pauses: PauseRow[];
}
interface Overview { today: string; items: Pausable[] }
interface LayoutContext { activeGymId: string | null }

const START_WINDOW = 30;
const OPEN_MAX = 90;

function rulesLine(p: Pausable): string {
  if (!p.rules.enabled) return "This can't be paused.";
  const days = p.remainingDays == null ? 'No limit on pause days'
    : p.remainingDays === 0 ? 'All pause days used'
    : `${p.remainingDays} of ${p.rules.maxTotalDays} pause days left`;
  return `${days} · ${p.rules.extendsValidity ? 'your end date moves with a pause' : 'your end date stays the same'}`;
}

const current = (p: Pausable) => p.pauses.find(x => x.phase === 'active') ?? p.pauses.find(x => x.phase === 'scheduled') ?? null;

export default function MemberPausesPage() {
  const { token } = useMemberAuth();
  const { activeGymId } = useOutletContext<LayoutContext>();
  const navigate = useNavigate();
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sheet, setSheet] = useState<Pausable | null>(null);

  const load = useCallback(() => {
    if (!token || !activeGymId) return;
    setError(null);
    mfetch<{ data: Overview }>(token, '/member/pauses', { params: { gymId: activeGymId } })
      .then(r => setData(r.data))
      .catch(e => setError(e instanceof MemberApiError ? e.message : 'Could not load'));
  }, [token, activeGymId]);
  useEffect(load, [load]);

  const act = async (label: string, path: string, done: string) => {
    if (!token || !activeGymId || !window.confirm(`${label}?`)) return;
    try {
      await mfetch(token, path, { method: 'POST', body: { gymId: activeGymId } });
      toast.success(done);
      load();
    } catch (e) {
      toast.error(e instanceof MemberApiError ? e.message : 'Something went wrong');
    }
  };

  return (
    <div className="mpage">
      <div className="row" style={{ paddingTop: 8, gap: 10 }}>
        <button className="icon-btn" aria-label="Back" onClick={() => navigate(-1)}>
          <Icon n="left" s={18} c="var(--ink-2)" />
        </button>
        <div className="dp dp-l">Pause</div>
      </div>

      {!data && !error && (
        <>
          <div className="skel" style={{ height: 120, borderRadius: 'var(--r-m)' }} />
          <div className="skel" style={{ height: 120, borderRadius: 'var(--r-m)' }} />
        </>
      )}
      {error && (
        <div className="card" style={{ padding: 18 }}>
          <div className="sub wrap">{error}</div>
          <button className="btn ghost sm" style={{ marginTop: 10 }} onClick={load}>Try again</button>
        </div>
      )}
      {data && data.items.length === 0 && (
        <div className="card flat" style={{ padding: 24, textAlign: 'center' }}>
          <div className="mid">Nothing to pause</div>
          <div className="sub">Your plan and packs at this gym show here.</div>
        </div>
      )}

      {data?.items.map(p => {
        const now = current(p);
        const past = p.pauses.filter(x => x.phase === 'completed');
        const canStart = p.rules.enabled && p.rules.memberCanPause && p.remainingDays !== 0 && !now;
        return (
          <div key={`${p.kind}:${p.id}`} className={`card${now?.phase === 'active' ? ' tint' : ''}`} style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div className="between" style={{ alignItems: 'flex-start', gap: 10 }}>
              <div className="col" style={{ minWidth: 0, gap: 1 }}>
                <div className="mid trunc">{p.name}</div>
                <div className="tiny trunc">{p.detail}{p.validTo ? ` · until ${fmtDateShort(p.validTo)}` : ''}</div>
              </div>
              {now?.phase === 'active' && <span className="tag gold">Paused</span>}
              {now?.phase === 'scheduled' && <span className="tag">Pause booked</span>}
            </div>

            {now ? (
              <>
                <div className="sub">
                  {now.phase === 'active' ? 'Paused' : 'Pausing'} {fmtDateShort(now.startDate)}
                  {now.endDate ? ` – ${fmtDateShort(now.endDate)}` : ' until you resume'}
                  {now.endDate && now.phase === 'active' ? ` · back ${fmtDateLong(addDaysStr(now.endDate, 1))}` : ''}
                </div>
                {now.extendedDays > 0 && <div className="tiny">Your end date moved by {now.extendedDays} {now.extendedDays === 1 ? 'day' : 'days'}.</div>}
                {now.phase === 'active' && now.startDate < data.today ? (
                  <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={() => act('Resume today', `/member/pauses/${p.kind}/${now.id}/resume`, 'Welcome back')}>Resume today</button>
                ) : (
                  <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={() => act('Cancel pause', `/member/pauses/${p.kind}/${now.id}/cancel`, 'Pause cancelled')}>Cancel pause</button>
                )}
              </>
            ) : (
              <>
                <div className="tiny">{rulesLine(p)}</div>
                {canStart && <button className="btn gold sm" style={{ alignSelf: 'flex-start' }} onClick={() => setSheet(p)}>Pause</button>}
                {p.rules.enabled && !p.rules.memberCanPause && <div className="tiny">Ask at the front desk to pause this.</div>}
              </>
            )}
            {past.length > 0 && <div className="tiny trunc">Paused {past.length} {past.length === 1 ? 'time' : 'times'} before · {p.usedDays} days used</div>}
          </div>
        );
      })}

      {sheet && data && token && activeGymId && (
        <PauseSheet item={sheet} today={data.today} token={token} gymId={activeGymId} onClose={() => setSheet(null)} onDone={() => { setSheet(null); toast.success('Paused'); load(); }} />
      )}
    </div>
  );
}

function PauseSheet({ item, today, token, gymId, onClose, onDone }: { item: Pausable; today: string; token: string; gymId: string; onClose: () => void; onDone: () => void }) {
  const lastStart = item.validTo && item.validTo < addDaysStr(today, START_WINDOW - 1) ? item.validTo : addDaysStr(today, START_WINDOW - 1);
  const starts = useMemo(() => {
    const out: string[] = [];
    for (let d = today; d <= lastStart; d = addDaysStr(d, 1)) out.push(d);
    return out;
  }, [today, lastStart]);
  const maxDays = item.remainingDays ?? OPEN_MAX;
  const [start, setStart] = useState(today);
  const [days, setDays] = useState(Math.min(7, maxDays));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const end = addDaysStr(start, days - 1);
  const needsReason = item.rules.requireReason && !reason.trim();
  const newValidTo = item.rules.extendsValidity && item.validTo ? addDaysStr(item.validTo, days) : null;

  const submit = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await mfetch(token, '/member/pauses', { method: 'POST', body: { gymId, kind: item.kind, id: item.id, startDate: start, endDate: end, reason: reason.trim() || undefined } });
      onDone();
    } catch (e) {
      setProblem(e instanceof MemberApiError ? e.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet onClose={onClose}>
      <div className="col" style={{ gap: 4 }}>
        <div className="dp dp-m trunc">Pause {item.name}</div>
        <div className="sub wrap">{rulesLine(item)}</div>
      </div>
      <div className="col" style={{ gap: 8 }}>
        <span className="lbl">Starts</span>
        <div className="hscroll">
          {starts.map(d => (
            <button key={d} className={`chip${d === start ? ' on' : ''}`} onClick={() => setStart(d)}>{d === today ? 'Today' : fmtDateShort(d)}</button>
          ))}
        </div>
      </div>
      <div className="between" style={{ gap: 12 }}>
        <div className="col" style={{ minWidth: 0 }}>
          <span className="lbl">For</span>
          <span className="sub trunc">{days} {days === 1 ? 'day' : 'days'} · back {fmtDateLong(addDaysStr(end, 1))}</span>
        </div>
        <Stepper value={days} step={1} onChange={v => setDays(Math.min(maxDays, Math.max(1, v)))} />
      </div>
      <div className="col" style={{ gap: 8 }}>
        <span className="lbl">{item.rules.requireReason ? 'Reason' : 'Reason (optional)'}</span>
        <input className="minput" value={reason} onChange={e => setReason(e.target.value)} placeholder="Travel, injury, exams…" maxLength={300} />
      </div>
      <div className="card flat" style={{ padding: 14 }}>
        <div className="sub wrap">
          {newValidTo ? `Your end date moves to ${fmtDateLong(newValidTo)}.` : item.validTo ? `Your end date stays ${fmtDateLong(item.validTo)}.` : 'Nothing is used while you are paused.'}
        </div>
        {item.weeklySlots > 0 && (
          <div className="tiny wrap" style={{ marginTop: 4 }}>
            Your weekly {item.weeklySlots === 1 ? 'session is' : 'sessions are'} not booked while paused, and come back after.
          </div>
        )}
      </div>
      {problem && <div className="sub wrap" style={{ color: 'var(--clay)' }}>{problem}</div>}
      <button className="btn gold block" disabled={busy || needsReason} onClick={submit}>
        {busy ? 'Pausing…' : needsReason ? 'Add a reason to pause' : `Pause for ${days} ${days === 1 ? 'day' : 'days'}`}
      </button>
    </Sheet>
  );
}
